/**
 * Consulenti che lavorano su una commessa, gestibili dalla commessa stessa.
 *
 * È lo stesso collegamento che si crea accettando un'offerta consulente o
 * dal pannello Consulenti: assegnando qualcuno qui gli si abilita anche
 * l'account per rendicontare i SAL.
 */
import { useEffect, useState } from 'react';
import { commesseConsulentiApi, consulentiApi } from '../api';

export default function ConsulentiCommessa({ commessaId }) {
  const [assegnati, setAssegnati] = useState([]);
  const [tutti, setTutti] = useState([]);
  const [scelto, setScelto] = useState('');
  const [lavorando, setLavorando] = useState(false);

  const carica = () => commesseConsulentiApi.list(commessaId).then(setAssegnati).catch(() => setAssegnati([]));
  useEffect(() => { carica(); }, [commessaId]);
  useEffect(() => { consulentiApi.list().then(setTutti).catch(() => setTutti([])); }, []);

  const aggiungi = async () => {
    if (!scelto) return;
    setLavorando(true);
    try { await commesseConsulentiApi.add(commessaId, Number(scelto)); setScelto(''); carica(); }
    catch (e) { alert(e); } finally { setLavorando(false); }
  };

  /**
   * Account già esistente ma non abilitato su questa commessa: capita quando
   * l'utenza nasce dopo l'assegnazione. Si risolve riassegnando, che crea il
   * collegamento mancante senza toccare il resto.
   */
  const abilita = async (c) => {
    setLavorando(true);
    try { await commesseConsulentiApi.add(commessaId, c.id); carica(); }
    catch (e) { alert(e); } finally { setLavorando(false); }
  };

  const togli = async (c) => {
    if (!confirm(`Togliere ${c.nome} ${c.cognome || ''} da questa commessa?\n\nPerderà anche l'accesso per rendicontare i SAL.`)) return;
    try { await commesseConsulentiApi.remove(commessaId, c.id); carica(); } catch (e) { alert(e); }
  };

  // Chi è già assegnato non va riproposto nel selettore
  const disponibili = tutti.filter(c => !assegnati.some(a => a.id === c.id));

  return (
    <div className="card space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-700">Consulenti assegnati</h3>
          <p className="text-xs text-gray-500">
            Chi lavora su questa commessa. L'assegnazione abilita anche la rendicontazione dei SAL.
          </p>
        </div>
      </div>

      {assegnati.length === 0 && (
        <p className="text-sm text-gray-400 text-center py-3 bg-gray-50 rounded border border-dashed">
          Nessun consulente assegnato
        </p>
      )}

      {assegnati.map(c => (
        <div key={c.id} className="flex items-center gap-2 bg-gray-50 border rounded px-3 py-2">
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-gray-800 truncate">
              {c.nome} {c.cognome}
              {c.specializzazione && <span className="font-normal text-gray-500"> — {c.specializzazione}</span>}
            </div>
            {/* Senza account il consulente non può entrare nel portale: va detto,
                altrimenti sembra assegnato ma non vedrebbe nulla. */}
            {!c.user_id ? (
              <div className="text-xs text-amber-700">
                ⚠ Nessun accesso al portale: creagli un utente in Utenti e Accessi per fargli rendicontare i SAL.
              </div>
            ) : !c.puo_rendicontare ? (
              <div className="text-xs text-amber-700">
                ⚠ Account presente ma non abilitato a questa commessa
                <button type="button" className="ml-1.5 text-brand-600 hover:underline font-medium"
                        disabled={lavorando} onClick={() => abilita(c)}>
                  abilitalo ora
                </button>
              </div>
            ) : (
              <div className="text-xs text-green-700">✓ Può rendicontare i SAL</div>
            )}
          </div>
          <button type="button" onClick={() => togli(c)}
                  className="text-gray-400 hover:text-red-600 px-1 shrink-0" title="Togli dalla commessa">✕</button>
        </div>
      ))}

      <div className="flex gap-2">
        <select className="input py-1 text-sm flex-1" value={scelto} onChange={e => setScelto(e.target.value)}>
          <option value="">— Aggiungi un consulente —</option>
          {disponibili.map(c => (
            <option key={c.id} value={c.id}>
              {c.nome} {c.cognome}{c.specializzazione ? ` — ${c.specializzazione}` : ''}
            </option>
          ))}
        </select>
        <button className="btn-primary btn-sm shrink-0" disabled={!scelto || lavorando} onClick={aggiungi}>
          {lavorando ? '…' : '+ Assegna'}
        </button>
      </div>
    </div>
  );
}
